import React from "react";
import useModels, { maxRes, fmtDims } from "@/lib/useModels";
import { AlertTriangle, Loader2, Expand, Download, RotateCcw, Film, Music, Heart, Share2, Trash2, Globe, Repeat } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, kindOf, downloadGen, fmtUSD, fmtINR, brand } from "@/lib/api";
import HoverVideo from "@/components/HoverVideo";

const Media = ({ g }) => {
  const src = mediaUrl(g.output_urls?.[0]);
  const k = kindOf(g);
  if (g.output_types?.[0] === "application/zip") return <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-[#64b7f6]" data-testid={`gen-zip-${g.id}`}><Film className="w-6 h-6" /><span className="text-xs font-mono">HDR EXR frames · ZIP</span></div>;
  if (k === "video") return <HoverVideo src={src} className="w-full h-full object-cover" testid={`gen-video-${g.id}`} />;
  if (k === "audio") return <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-4"><Music className="w-6 h-6 text-[#7AF25F]" /><audio src={src} controls className="w-full" /></div>;
  return <img src={src} alt={g.prompt} loading="lazy" className="w-full h-full object-cover" />;
};

const goFullscreen = (box, g, onOpen) => {
  const el = box.querySelector("video");
  if (!el) { if (g.status === "succeeded") onOpen(g); return; }
  el.controls = true; el.muted = false; el.play?.();
  (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
};

const shareGen = async (g, onChanged) => {
  let token = g.share_token;
  if (!g.share_enabled) {
    const { data } = await api.post(`/generations/${g.id}/share`);
    token = data.share_token;
    onChanged?.();
  }
  const url = `${window.location.origin}/share/${token}`;
  try { await navigator.clipboard.writeText(url); toast.success("Public link copied — anyone with it can view"); }
  catch { toast.message("Public link", { description: url }); }
};

const publishGen = async (g, onChanged) => {
  try {
    const { data } = await api.post(`/generations/${g.id}/publish`, { published: !g.published });
    toast.success(data.published ? "Published to the home page" : "Removed from the home page");
    onChanged?.();
  } catch (e) { toast.error(e?.response?.data?.detail || "Could not change publishing"); }
};

const ResLine = ({ g, model }) => {
  const o = (g.output_paths || {})["0"] || {};
  const dims = fmtDims(o.w, o.h);
  const mx = maxRes(model);
  if (!dims && !mx && !g.settings?.resolution) return null;
  return (
    <div className="flex items-center gap-2 mt-1 text-[10px] font-mono text-zinc-500" data-testid={`gen-res-${g.id}`}>
      {dims && <span className="text-zinc-300">{dims}</span>}
      {g.settings?.resolution && <span>{g.settings.resolution}</span>}
      {g.settings?.aspect_ratio && <span>{g.settings.aspect_ratio}</span>}
      {mx && <span className="ml-auto" title="Highest resolution this model can make">max {mx}</span>}
    </div>
  );
};

const GenCard = ({ g, onOpen, onRetry, onChanged, model }) => (
  <div data-testid={`gen-card-${g.id}`}
    className={`ax-gen-card group ${g.status === "running" ? "is-running" : g.status === "failed" ? "is-failed" : ""}`}>
    <div className={`relative ${["video", "remix", "blackfx"].includes(g.section) ? "aspect-video" : "aspect-square"} bg-[#0D0D0F] select-none`}
      data-testid={`gen-media-${g.id}`} onClick={(e) => { if (g.status === "succeeded" && !e.target.closest("button")) onOpen(g); }}
      onDoubleClick={(e) => goFullscreen(e.currentTarget, g, onOpen)} title={g.status === "succeeded" ? "Click to open · double-click for fullscreen" : ""}>
      {g.status === "succeeded" && <Media g={g} />}
      {g.status === "succeeded" && (
        <button type="button" onClick={() => onRetry(g)} title="Recreate — same prompt, settings and media" data-testid={`gen-recreate-${g.id}`}
          className="absolute bottom-2 left-2 h-7 px-2 rounded-md bg-black/75 text-[11px] text-white flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black">
          <Repeat className="w-3 h-3" />Recreate</button>
      )}
      {g.status === "running" && (
        <div className="absolute inset-0 ax-shimmer flex flex-col items-center justify-center gap-2 text-zinc-400" data-testid={`gen-running-${g.id}`}>
          <Loader2 className="w-5 h-5 animate-spin text-[#7AF25F]" />
          <span className="text-xs font-mono" data-testid={`gen-status-${g.id}`}>{g.queued ? "Queued — starts when a slot frees" : g.section === "blackfx" ? "Processing…" : "Generating…"}</span>
          <span className="text-[10px] text-zinc-600">{["video", "remix", "blackfx"].includes(g.section) ? "Video takes 1–5 min" : "Usually 5–30 s"}</span>
        </div>
      )}
      {g.status === "failed" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center" data-testid={`gen-failed-${g.id}`}>
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="text-xs text-red-300 line-clamp-4">{g.error || "Generation failed"}</span>
          <button onClick={() => onRetry(g)} className="text-[11px] font-mono uppercase tracking-widest text-zinc-300 hover:text-white flex items-center gap-1 mt-1" data-testid={`gen-retry-${g.id}`}>
            <RotateCcw className="w-3 h-3" /> Use these settings
          </button>
        </div>
      )}
      {g.status === "succeeded" && (
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/80 via-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end gap-1.5 p-2 [&>button]:pointer-events-auto">
          <button onClick={async () => { await api.post(`/generations/${g.id}/like`); onChanged?.(); }} data-testid={`gen-like-${g.id}`} className="ax-icon-btn" title={g.liked ? "Unlike" : "Like"}>
            <Heart className={`w-3.5 h-3.5 ${g.liked ? "fill-pink-400 text-pink-400" : ""}`} /></button>
          <button onClick={() => publishGen(g, onChanged)} data-testid={`gen-publish-${g.id}`} aria-pressed={!!g.published}
            className={`ax-icon-btn ${g.published ? "!bg-[#7AF25F]/80 !text-black" : ""}`} title={g.published ? "Unpublish from home page" : "Publish to home page"}><Globe className="w-3.5 h-3.5" /></button>
          <button onClick={() => shareGen(g, onChanged)} data-testid={`gen-share-${g.id}`} className="ax-icon-btn" title="Copy public share link"><Share2 className="w-3.5 h-3.5" /></button>
          <button onClick={async () => { await api.post(`/generations/${g.id}/trash`); toast.success("Moved to Recycle Bin"); onChanged?.(); }} data-testid={`gen-delete-${g.id}`} className="ax-icon-btn hover:!bg-red-500/60" title="Move to Recycle Bin"><Trash2 className="w-3.5 h-3.5" /></button>
          <button onClick={() => downloadGen(g)} data-testid={`gen-download-${g.id}`} className="ax-icon-btn" title="Download"><Download className="w-3.5 h-3.5" /></button>
          <button onClick={() => onOpen(g)} data-testid={`gen-expand-${g.id}`} className="ax-icon-btn" title="Open"><Expand className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {g.output_urls?.length > 1 && <span className="absolute top-2 left-2 ax-badge !bg-black/75 !text-white" data-testid="gen-outputs-badge">{g.output_urls.length} outputs</span>}
      {g.published && <span className={`absolute ${g.output_urls?.length > 1 ? "top-8" : "top-2"} left-2 ax-badge !bg-black/75 !text-[#7AF25F] !border-[#7AF25F]/40 flex items-center gap-1`} data-testid={`gen-published-${g.id}`}><Globe className="w-2.5 h-2.5" />Public</span>}
      {g.liked && <Heart className="absolute top-2 right-2 w-4 h-4 fill-pink-400 text-pink-400 drop-shadow" data-testid={`gen-liked-${g.id}`} />}
      {g.status !== "succeeded" && (
        <button onClick={async () => { await api.post(`/generations/${g.id}/trash`); onChanged?.(); }} data-testid={`gen-delete-${g.id}`} title="Move to Recycle Bin"
          className="absolute top-2 right-2 ax-icon-btn opacity-0 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
      )}
    </div>
    <button onClick={() => g.status === "succeeded" && onOpen(g)} className="w-full text-left p-3">
      <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500">
        <span className="truncate">{brand(g.model_name)}</span>
        <span>{fmtINR(g.cost_inr)} · {fmtUSD(g.cost_usd)}</span>
      </div>
      {g.status === "succeeded" && <ResLine g={g} model={model} />}
    </button>
  </div>
);

export default function GenerationGrid({ items, onOpen, onRetry, onChanged, emptyTitle, emptyHint, cols = "grid-cols-2 md:grid-cols-3 xl:grid-cols-4" }) {
  const models = useModels();
  if (!items.length) {
    return (
      <div className="h-full min-h-[320px] flex flex-col items-center justify-center text-center gap-3" data-testid="history-empty">
        <div className="h-14 w-14 rounded-2xl border border-zinc-800 bg-[#121214] flex items-center justify-center"><Film className="w-6 h-6 text-zinc-600" /></div>
        <div className="font-heading text-lg text-zinc-200">{emptyTitle}</div>
        <p className="text-sm text-zinc-500 max-w-sm">{emptyHint}</p>
      </div>
    );
  }
  return (
    <div className={`grid ${cols} gap-3`} data-testid="history-grid">
      {items.map((g) => <GenCard key={g.id} g={g} onOpen={onOpen} onRetry={onRetry} onChanged={onChanged} model={models.find((m) => m.id === g.model_id)} />)}
    </div>
  );
}
